import { flatten } from "lodash";
import { Post, PostStates } from "./types";
import {
  editDraft,
  editQueuedPost,
  editScheduledPost,
  moveDraftToSchedule,
  moveDraftToQueue,
  moveQueueToDraft,
  moveQueueToSchedule,
  moveScheduleToDraft,
  moveScheduledPostToQueue,
  createScheduledPost,
  createDraft,
  createQueuedPost,
  editDraftValidation,
} from "api/post";

export const savePost =
  (posts: Post | Post[], previousPost: Post) => async () => {
    // todo: when we put all posts to the store we should get previous from it
    // const previousPost = parsePost(previousPostData);
    if (Array.isArray(posts)) {
      const { editPost, newPosts }: { editPost?: Post; newPosts: Post[] } =
        posts.reduce(
          (acc: { editPost?: Post; newPosts: Post[] }, post) => {
            // eslint-disable-next-line unicorn/no-negated-condition
            if (!post._id) {
              acc.newPosts.push(post);
            } else {
              acc.editPost = post;
            }

            return acc;
          },
          { editPost: undefined, newPosts: [] }
        );
      let updatedIds: string[] = [];
      if (editPost) {
        updatedIds = await PostEditingMap[previousPost.state][editPost.state](
          editPost,
          previousPost
        );
      }

      const createdPostResult = await Promise.allSettled(
        newPosts.map(async (post) => {
          const rs = await PostCreatingMap[post.state](post);
          return {
            state: post.state,
            data: rs,
          };
        })
      );

      return {
        ids: [
          ...createdPostResult.flatMap((c) =>
            c.status === "fulfilled" && c.value.data ? c.value.data : []
          ),
          ...updatedIds,
        ],
      };
    }

    const rs = await PostEditingMap[previousPost.state][posts.state](
      posts,
      previousPost
    );

    return {
      ids: rs,
    };
  };

const PostCreatingMap = {
  [PostStates.draft]: createDraft,
  [PostStates.schedule]: createScheduledPost,
  [PostStates.queue]: createQueuedPost,
  [PostStates.archived]: async () => {
    return [];
  }, // archived draft is not creatable
} as const;

type MakePostFunc = (post: Post, previousPost?: Post) => Promise<string[]>;

interface MakePostService {
  [PostStates.draft]: MakePostFunc;
  [PostStates.schedule]: MakePostFunc;
  [PostStates.queue]: MakePostFunc;
  [PostStates.archived]: MakePostFunc;
}

const PostEditingMap = {
  [PostStates.draft]: {
    [PostStates.draft]: async (post: Post) => {
      await editDraft(post);
      return [post._id];
    },
    [PostStates.schedule]: async (post: Post) => {
      await editDraft(post);
      const rs = await moveDraftToSchedule(post);
      return flatten(rs);
    },
    [PostStates.queue]: async (post: Post) => {
      await editDraft(post);
      return await moveDraftToQueue(post._id);
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
  [PostStates.schedule]: {
    [PostStates.schedule]: editScheduledPost,
    [PostStates.draft]: async (post: Post) => {
      await editDraftValidation(post);
      const newId = await moveScheduleToDraft(post._id);
      await editDraft({ ...post, _id: newId }, { validate: false });
      return [newId];
    },
    [PostStates.queue]: async (post: Post) => {
      const ids = await moveScheduledPostToQueue(post._id);
      return await editQueuedPost({ ...post, _id: ids[0] });
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
  [PostStates.archived]: {
    [PostStates.schedule]: (post: Post, previousPost: Post | undefined) => {
      if (previousPost?.isSuccess) {
        return PostCreatingMap[post.state](post);
      }

      return editScheduledPost(post);
    },
    [PostStates.draft]: async (post: Post, previousPost: Post | undefined) => {
      if (previousPost?.isSuccess) {
        return PostCreatingMap[post.state](post);
      }

      await editDraftValidation(post);
      const newId = await moveScheduleToDraft(post._id);
      await editDraft({ ...post, _id: newId }, { validate: false });
      return [newId];
    },
    [PostStates.queue]: async (post: Post, previousPost: Post | undefined) => {
      if (previousPost?.isSuccess) {
        return PostCreatingMap[post.state](post);
      }

      const ids = await moveScheduledPostToQueue(post._id);
      return await editQueuedPost({ ...post, _id: ids[0] });
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
  [PostStates.queue]: {
    [PostStates.queue]: editQueuedPost,
    [PostStates.draft]: async (post: Post) => {
      await editDraftValidation(post);
      const newId = await moveQueueToDraft(post._id!);
      await editDraft({ ...post, _id: newId }, { validate: false });
      return [newId];
    },
    [PostStates.schedule]: async (post: Post) => {
      const rs = await moveQueueToSchedule(post);
      return rs.data;
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
} as const;
